/**
 * BOM Intake API client (Phase 3c Step 9).
 * FE-owned parse — server receives staging lines only.
 */
import type {
  BomIntakeSession,
  BomIntakeSessionDetail,
  BomIntakeStagingLine,
  BomSourceFile,
  ParsedBomIntakeLine,
} from './types'

export type BomIntakeApiError = {
  ok: false
  error: { code: string; message: string; status: number }
}

export class BomIntakeApiClientError extends Error {
  readonly code: string
  readonly status: number

  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'BomIntakeApiClientError'
    this.code = code
    this.status = status
  }
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfApiError(res: Response, body: unknown, fallback: string): void {
  if (res.ok) return
  const err = body as BomIntakeApiError
  throw new BomIntakeApiClientError(
    err.error?.code ?? 'HTTP_ERROR',
    err.error?.message ?? `${fallback} (${res.status})`,
    err.error?.status ?? res.status,
  )
}

export async function createSourceFileApi(input: {
  filename: string
  content_meta?: Record<string, unknown> | null
  uploaded_by?: string | null
}): Promise<BomSourceFile> {
  const res = await fetch('/api/bom-intake/source-files', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  const body = await parseJson<
    { ok: true; source_file: BomSourceFile } | BomIntakeApiError
  >(res)
  throwIfApiError(res, body, 'Tạo source file thất bại')
  return (body as { source_file: BomSourceFile }).source_file
}

export async function createSessionApi(input: {
  source_file_id?: string | null
  code_hint?: string | null
  product_id?: string | null
  created_by?: string | null
}): Promise<BomIntakeSession> {
  const res = await fetch('/api/bom-intake/sessions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  const body = await parseJson<
    { ok: true; session: BomIntakeSession } | BomIntakeApiError
  >(res)
  throwIfApiError(res, body, 'Tạo session thất bại')
  return (body as { session: BomIntakeSession }).session
}

export async function getSessionApi(
  id: string,
): Promise<BomIntakeSessionDetail | null> {
  const res = await fetch(`/api/bom-intake/sessions/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<
    { ok: true; session: BomIntakeSessionDetail } | BomIntakeApiError
  >(res)
  throwIfApiError(res, body, 'Tải session thất bại')
  return (body as { session: BomIntakeSessionDetail }).session
}

export async function stageLinesApi(
  sessionId: string,
  lines: ParsedBomIntakeLine[],
): Promise<BomIntakeStagingLine[]> {
  const res = await fetch(
    `/api/bom-intake/sessions/${encodeURIComponent(sessionId)}/lines`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lines }),
    },
  )
  const body = await parseJson<
    { ok: true; lines: BomIntakeStagingLine[] } | BomIntakeApiError
  >(res)
  throwIfApiError(res, body, 'Staging dòng thất bại')
  return (body as { lines: BomIntakeStagingLine[] }).lines
}

export async function patchLineApi(
  sessionId: string,
  lineId: string,
  patch: {
    material_id?: string | null
    packaging_assembly_type?: 'assembled' | 'kd' | null
    packaging_ista_level?: '1A' | '3A' | null
  },
): Promise<BomIntakeStagingLine> {
  const res = await fetch(
    `/api/bom-intake/sessions/${encodeURIComponent(sessionId)}/lines/${encodeURIComponent(lineId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
  )
  const body = await parseJson<
    { ok: true; line: BomIntakeStagingLine } | BomIntakeApiError
  >(res)
  throwIfApiError(res, body, 'Cập nhật dòng thất bại')
  return (body as { line: BomIntakeStagingLine }).line
}

export async function commitSessionApi(
  sessionId: string,
  input: {
    name?: string | null
    version_label?: string | null
    notes?: string | null
    actor?: string | null
    modes?: ('assembled' | 'kd')[] | null
  } = {},
): Promise<{
  session_id: string
  bom: { id: string; status: string; name: string }
  boms?: Array<{ id: string; status: string; name: string }>
}> {
  const res = await fetch(
    `/api/bom-intake/sessions/${encodeURIComponent(sessionId)}/commit`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  )
  const body = await parseJson<
    | {
        ok: true
        session_id: string
        bom: { id: string; status: string; name: string }
        boms?: Array<{ id: string; status: string; name: string }>
      }
    | BomIntakeApiError
  >(res)
  throwIfApiError(res, body, 'Commit BOM Intake thất bại')
  return body as {
    session_id: string
    bom: { id: string; status: string; name: string }
    boms?: Array<{ id: string; status: string; name: string }>
  }
}

export async function commitPackagingSessionApi(
  sessionId: string,
  input: {
    name?: string | null
    notes?: string | null
  } = {},
): Promise<{
  session_id: string
  packaging_bom: { id: string; status: string; name: string }
}> {
  const res = await fetch(
    `/api/bom-intake/sessions/${encodeURIComponent(sessionId)}/commit-packaging`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  )
  const body = await parseJson<
    | {
        ok: true
        session_id: string
        packaging_bom: { id: string; status: string; name: string }
      }
    | BomIntakeApiError
  >(res)
  throwIfApiError(res, body, 'Commit Packaging Intake thất bại')
  return body as {
    session_id: string
    packaging_bom: { id: string; status: string; name: string }
  }
}

export async function discardSessionApi(sessionId: string): Promise<BomIntakeSession> {
  const res = await fetch(
    `/api/bom-intake/sessions/${encodeURIComponent(sessionId)}/discard`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    },
  )
  const body = await parseJson<
    { ok: true; session: BomIntakeSession } | BomIntakeApiError
  >(res)
  throwIfApiError(res, body, 'Discard session thất bại')
  return (body as { session: BomIntakeSession }).session
}

export async function applyStandardizedNamesApi(
  sessionId: string,
  changes: Array<{ line_id: string; proposed_name: string }>,
): Promise<BomIntakeStagingLine[]> {
  const res = await fetch(
    `/api/bom-intake/sessions/${encodeURIComponent(sessionId)}/apply-standardized-names`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ changes }),
    },
  )
  const body = await parseJson<
    { ok: true; lines: BomIntakeStagingLine[] } | BomIntakeApiError
  >(res)
  throwIfApiError(res, body, 'Áp dụng tên chuẩn hóa thất bại')
  return (body as { lines: BomIntakeStagingLine[] }).lines
}

